import { inject, DestroyRef } from '@angular/core';
import {
  CanActivateFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot
} from '@angular/router';
import {
  defer,
  from,
} from 'rxjs';
import {
  filter,
  map,
  take,
  switchMap,
  finalize
} from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingController } from '@ionic/angular';
import {
  AuthenticationService,
  AuthenticatedUser
} from '../services/authentication.service';

// Set to true to skip showing the loading spinner
const SKIP_AUTH_LOADING = true;

export const authenticatedCanActivateGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const auth = inject(AuthenticationService);
  const router = inject(Router);
  const destroyRef = inject(DestroyRef);
  const loadingCtrl = inject(LoadingController);
  const fallback = (route.data['redirectTo'] as string) ?? '/splash';

  return defer(() => {
    if (SKIP_AUTH_LOADING) {
      // Skip loading, just check auth directly
      return auth.currentUser$.pipe(
        filter((u): u is AuthenticatedUser | null => u !== undefined),
        take(1),
        map(user => user ? true : router.createUrlTree([fallback])),
        takeUntilDestroyed(destroyRef)
      );
    }

    let loaderEl: HTMLIonLoadingElement;

    // Show loading spinner as before
    return from(loadingCtrl.create({
      message: 'Checking authentication…',
      spinner: 'crescent'
    })).pipe(
      switchMap(loader => {
        loaderEl = loader;
        return from(loader.present());
      }),
      switchMap(() =>
        auth.currentUser$.pipe(
          filter((u): u is AuthenticatedUser | null => u !== undefined),
          take(1),
          map(user => user ? true : router.createUrlTree([fallback]))
        )
      ),
      finalize(() => {
        loaderEl?.dismiss();
      }),
      takeUntilDestroyed(destroyRef)
    );
  });
};